<template>
    <div>
        <div class="ds-employee-form-section relative">
            <div class="ds-employee-form-section-divider mb-2">
                <div class="ds-employee-form-section-title">Основные данные</div>
            </div>
            <div v-if="authStore.permissions?.can_edit_user_main_info && !edit" class="flex justify-end mb-2">
                <div class="ds-employee-form-edit-link cursor-pointer" @click="change">
                    Изменить
                </div>
            </div>
            <div v-if="edit" class="ds-employee-form-data-grid">
                <div class="ds-employee-form-data-column">
                    <base-input v-model="lastName" max-length="20">Фамилия</base-input>
                    <base-input v-model="firstName" max-length="20">Имя</base-input>
                    <base-input v-model="middleName" max-length="20">Отчество</base-input>
                </div>
                <div class="ds-employee-form-data-column">
                    <phone-input v-model="phone">Телефон</phone-input>
                    <base-input v-model="email" max-length="50">E-mail</base-input>
                </div>
            </div>
            <div v-else class="ds-employee-form-data-grid">
                <div class="ds-employee-form-data-column">
                    <EmployeeDataItem label="Фамилия" :value="lastName" />
                    <EmployeeDataItem label="Имя" :value="firstName" />
                    <EmployeeDataItem label="Отчество" :value="middleName" v-if="middleName" />
                </div>
                <div class="ds-employee-form-data-column">
                    <EmployeeDataItem label="Телефон" :value="phone" />
                    <EmployeeDataItem label="E-mail" :value="email" v-if="email"/>
                </div>
            </div>
            <checkbox
                v-if="authStore.permissions?.can_change_smart_checkin_possible"
                class="text-sm mt-3"
                :model-value="smartCheckinPossible"
                :disabled="isSmartCheckinSaving"
                @update:model-value="saveSmartCheckinPossible"
            >СмартОтметка</checkbox>
        </div>
        <div class="pt-3 text-right" v-if="edit">
            <btn @click="save" :disabled="isDisabled" :class="{'opacity-50': isDisabled}">Сохранить</btn>
            <btn class="ml-3" type="secondary" @click="cancel">Отмена</btn>
        </div>       
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import EmployeeDataItem from './EmployeeDataItem.vue'
import { useEmploymentStore } from '@/stores/employmentStore'
import { useAuthStore } from '@/stores/authStore'

interface Props {
    user: {
        id: string
        first_name: string
        last_name: string
        middle_name?: string
        phone: string
        email?: string
        smart_checkin_possible?: boolean
    }
}

interface Data {
    first_name?: string,
    last_name?: string,
    middle_name?: string,
    email?: string,
    phone?: string
}

const store = useEmploymentStore()
const authStore = useAuthStore()
let { user } = defineProps<Props>()
const emit = defineEmits(['edit'])
const edit = ref(false)

const email = ref(user.email)
const phone = ref(user.phone)
const firstName = ref(user.first_name)
const lastName = ref(user.last_name)
const middleName = ref(user.middle_name || '')
const smartCheckinPossible = ref(user.smart_checkin_possible ?? true)
const isSmartCheckinSaving = ref(false)
const originalMiddleName = computed(() => user.middle_name || '')

const hasChanges = computed(() => {
  return (
    firstName.value !== user.first_name ||
    lastName.value !== user.last_name ||
    middleName.value !== originalMiddleName.value ||
    (email.value && email.value !== user.email) ||
    phone.value !== user.phone
  )
})

const valid = computed(() => {
    const re = /^[A-Z0-9._%+-]+@[A-Z0-9-]+.+.[A-Z]{2,4}$/i
    return (firstName.value.length > 1 &&
        lastName.value.length > 1 &&
        (!email.value || re.test(email.value))
    ) 
})

const isDisabled = computed(() => !(hasChanges.value && valid.value))

const save = async () => {
    let data:Data = {}
    if(firstName.value !== user.first_name) data.first_name = firstName.value
    if(lastName.value !== user.last_name) data.last_name = lastName.value
    if(middleName.value !== originalMiddleName.value) data.middle_name = middleName.value
    if(email.value && email.value !== user.email) data.email = email.value
    if(phone.value !== user.phone) data.phone = phone.value
    await store.userEdit(user.id, data)
    emit('edit')
    edit.value = false
}

const change = () => {
    if(authStore.permissions?.can_edit_user_main_info) {
        edit.value = true
    }
}

const saveSmartCheckinPossible = async (value: boolean) => {
    const previousValue = smartCheckinPossible.value
    smartCheckinPossible.value = value
    isSmartCheckinSaving.value = true
    const error = await store.userEdit(user.id, { smart_checkin_possible: value })
    isSmartCheckinSaving.value = false

    if (error) smartCheckinPossible.value = previousValue
}

const resetUserData = () => {
    lastName.value = user.last_name
    email.value = user.email
    phone.value = user.phone
    firstName.value = user.first_name
    middleName.value = user.middle_name || ''
}

const cancel = () => {
    edit.value = false
    resetUserData()
}

</script>
